import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { verifyBusinessInvite } from '../../util/api';
import { isInviteOnlyUserType, readInviteToken } from '../../util/userTypes';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { setCurrentUser } from '../../ducks/user.duck';

// Must mirror what user.duck requests when it fetches currentUser: the
// response replaces the user in the store (same as newsletterPrompt.duck).
const PROFILE_FIELDS = {
  expand: true,
  include: ['profileImage'],
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.square-xsmall',
    'variants.square-xsmall2x',
  ],
};

/**
 * Record that the post-signup welcome (/get-started) has been shown, on the
 * profile so it follows the user across devices: once stamped, the page
 * redirects to the homepage forever after, and a returning user never sees
 * it. Stamped when the welcome renders, which is why every way of leaving
 * it (button, link, Escape, backdrop) counts as "shown".
 */
export const markOnboardingNudgeShownThunk = createAsyncThunk(
  'AuthenticationPage/markOnboardingNudgeShown',
  async (_arg, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.currentUser.updateProfile(
        { privateData: { onboardingNudgeShownAt: new Date().toISOString() } },
        PROFILE_FIELDS
      );
      const entities = denormalisedResponseEntities(response);
      if (entities.length === 1) {
        dispatch(setCurrentUser(entities[0]));
      }
      return null;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
// A failed stamp only means the welcome may show once more next time.
export const markOnboardingNudgeShown = () => dispatch =>
  dispatch(markOnboardingNudgeShownThunk())
    .unwrap()
    .catch(() => null);

/**
 * Ask our server whether an invite link admits the user type its URL names.
 * The secret that signs invites never leaves the server, so this is the only
 * way the page can know; the result is what AuthenticationPage checks before
 * letting an invite-only type past the signup guard.
 */
export const verifyInvite = createAsyncThunk(
  'AuthenticationPage/verifyInvite',
  async ({ token, userType, marketplaceRootURL }, { rejectWithValue }) => {
    try {
      const result = await verifyBusinessInvite(token, marketplaceRootURL);
      const valid = result?.valid === true && result.userType === userType;
      return { token, userType, valid, email: valid ? result.email || null : null };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const authenticationPageSlice = createSlice({
  name: 'AuthenticationPage',
  initialState: {
    // { token, userType, valid, email } for the last verified link, or null.
    invite: null,
    inviteInProgress: false,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(verifyInvite.pending, state => {
        state.inviteInProgress = true;
        state.invite = null;
      })
      .addCase(verifyInvite.fulfilled, (state, action) => {
        state.inviteInProgress = false;
        state.invite = action.payload;
      })
      .addCase(verifyInvite.rejected, (state, action) => {
        // A server that could not be asked admits nobody.
        const { token, userType } = action.meta.arg;
        state.inviteInProgress = false;
        state.invite = { token, userType, valid: false, email: null };
      });
  },
});

export default authenticationPageSlice.reducer;

export const TOS_ASSET_NAME = 'terms-of-service';
export const PRIVACY_POLICY_ASSET_NAME = 'privacy-policy';

export const loadData = (params, search, config) => dispatch => {
  const pageAsset = {
    termsOfService: `content/pages/${TOS_ASSET_NAME}.json`,
    privacyPolicy: `content/pages/${PRIVACY_POLICY_ASSET_NAME}.json`,
  };

  // Only /signup/<invite-only type>?invite=… asks; every other route to this
  // page, the public signup included, never makes the call.
  const userType = params?.userType;
  const token = readInviteToken(search);
  const invitePromise =
    isInviteOnlyUserType(userType) && token
      ? dispatch(verifyInvite({ token, userType, marketplaceRootURL: config?.marketplaceRootURL }))
      : Promise.resolve(null);

  return Promise.all([dispatch(fetchPageAssets(pageAsset, true)), invitePromise]);
};
